import * as React from 'react';
import { Play, Pause, Volume2, Volume1, VolumeX, Maximize2 } from './icons.js';

// Simple video controller context
type Ctx = {
  video: HTMLVideoElement | null;
  setVideo: (el: HTMLVideoElement | null) => void;
  playing: boolean;
  muted: boolean;
  volume: number; // 0..1
  duration: number; // seconds
  currentTime: number; // seconds
  controlsVisible: boolean;
  actions: {
    playPause: () => void;
    seek: (time: number) => void;
    seekBy: (delta: number) => void;
    setMuted: (m: boolean) => void;
    setVolume: (v: number) => void;
    requestFullscreen: () => void;
  };
};

const VideoCtx = React.createContext<Ctx | null>(null);

function useVideoCtx() {
  const ctx = React.useContext(VideoCtx);
  if (!ctx) throw new Error('Video controls must be used within <VideoPlayer>');
  return ctx;
}

export type VideoPlayerProps = React.PropsWithChildren<{
  className?: string;
  style?: React.CSSProperties;
}>;

export function VideoPlayer({ className, style, children }: VideoPlayerProps) {
  const [video, setVideo] = React.useState<HTMLVideoElement | null>(null);
  const [playing, setPlaying] = React.useState(false);
  const [muted, setMutedState] = React.useState(false);
  const [volume, setVolumeState] = React.useState(1);
  const [duration, setDuration] = React.useState(0);
  const [currentTime, setCurrentTime] = React.useState(0);

  // Event wiring
  React.useEffect(() => {
    if (!video) return;
    const onPlay = () => setPlaying(true);
    const onPause = () => setPlaying(false);
    const onVolume = () => {
      setMutedState(video.muted);
      setVolumeState(video.volume);
    };
    const onLoaded = () => {
      const newDuration = Number.isFinite(video.duration) ? video.duration : 0;
      if (newDuration !== duration) {
        setDuration(newDuration);
      }
    };
    const onTime = () => setCurrentTime(video.currentTime);
    
    // Mobile-specific event handling
    const onCanPlay = () => {
      // Ensure we have proper metadata on mobile
      onLoaded();
      onTime();
    };
    
    const onLoadStart = () => {
      // Reset states when loading starts
      setDuration(0);
      setCurrentTime(0);
    };

    video.addEventListener('play', onPlay);
    video.addEventListener('pause', onPause);
    video.addEventListener('volumechange', onVolume);
    video.addEventListener('loadedmetadata', onLoaded);
    video.addEventListener('durationchange', onLoaded);
    video.addEventListener('timeupdate', onTime);
    video.addEventListener('canplay', onCanPlay);
    video.addEventListener('loadstart', onLoadStart);
    
    // initialize
    onVolume();
    onLoaded();
    onTime();
    setPlaying(!video.paused);
    
    return () => {
      video.removeEventListener('play', onPlay);
      video.removeEventListener('pause', onPause);
      video.removeEventListener('volumechange', onVolume);
      video.removeEventListener('loadedmetadata', onLoaded);
      video.removeEventListener('durationchange', onLoaded);
      video.removeEventListener('timeupdate', onTime);
      video.removeEventListener('canplay', onCanPlay);
      video.removeEventListener('loadstart', onLoadStart);
    };
  }, [video, duration]);

  // Visibility management
  const containerRef = React.useRef<HTMLDivElement>(null);
  const [controlsVisible, setControlsVisible] = React.useState(true);
  // Keep container borderRadius in sync with the <video> so overlays clip properly
  React.useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const sync = () => {
      const vid = el.querySelector('video') as HTMLVideoElement | null;
      if (!vid) return;
      const br = getComputedStyle(vid).borderRadius;
      if (br && el.style.borderRadius !== br) {
        el.style.borderRadius = br;
      }
      // Always ensure overlays are clipped
      if (el.style.overflow !== 'hidden') el.style.overflow = 'hidden';
    };
    sync();
    const ro = new ResizeObserver(() => sync());
    ro.observe(el);
    const vid = el.querySelector('video');
    if (vid) ro.observe(vid);
    return () => ro.disconnect();
  }, []);
  React.useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    let hideTimer: any;
    const onMove = () => {
      setControlsVisible(true);
      clearTimeout(hideTimer);
      hideTimer = setTimeout(() => setControlsVisible(false), 1800);
    };
    el.addEventListener('mousemove', onMove);
    el.addEventListener('touchstart', onMove);
    hideTimer = setTimeout(() => setControlsVisible(false), 1800);
    return () => {
      clearTimeout(hideTimer);
      el.removeEventListener('mousemove', onMove);
      el.removeEventListener('touchstart', onMove);
    };
  }, []);

  const actions = React.useMemo<Ctx['actions']>(() => ({
    playPause: () => {
      if (!video) return;
      if (video.paused) {
        const playPromise = video.play();
        if (playPromise !== undefined) {
          playPromise.catch(err => {
            // eslint-disable-next-line no-console
            console.warn('Play failed:', err);
          });
        }
      } else {
        video.pause();
      }
    },
    seek: (t: number) => {
      if (!video || !Number.isFinite(t)) return;
      video.currentTime = Math.max(0, Math.min(duration || Number.MAX_SAFE_INTEGER, t));
    },
    seekBy: (d: number) => {
      if (!video) return;
      const next = video.currentTime + d;
      video.currentTime = Math.max(0, Math.min(duration || Number.MAX_SAFE_INTEGER, next));
    },
    setMuted: (m: boolean) => {
      if (!video) return;
      video.muted = m;
    },
    setVolume: (v: number) => {
      if (!video) return;
      const nv = Math.max(0, Math.min(1, v));
      video.volume = nv;
      if (nv > 0 && video.muted) video.muted = false;
    },
    requestFullscreen: () => {
      if (!video) return;
      const el: any = video;
      (el.requestFullscreen || el.webkitRequestFullscreen || el.msRequestFullscreen || (() => {})).call(el);
    },
  }), [video, duration]);

  const ctx: Ctx = React.useMemo(() => ({ video, setVideo, playing, muted, volume, duration, currentTime, controlsVisible, actions }), [video, playing, muted, volume, duration, currentTime, controlsVisible, actions]);

  // Provide default CSS variables inline so styling works even if Tailwind
  // does not scan this library's class names in consumer builds.
  const baseVars = React.useMemo(() => ({
    ['--os-video-fg' as any]: '255 255 255',
    ['--os-video-ring' as any]: '255 255 255',
    ['--os-video-overlay' as any]: '0 0 0',
    ['--os-video-overlay-alpha' as any]: '0.60',
  }) as React.CSSProperties, []);

  return (
    <VideoCtx.Provider value={ctx}>
      <div
        ref={containerRef}
        // Avoid reliance on Tailwind in consumer builds; inline critical layout styles
        className={className}
        style={{ position: 'relative', width: '100%', height: '100%', backgroundColor: 'black', overflow: 'hidden', ...baseVars, ...style }}
      >
        {children}
      </div>
    </VideoCtx.Provider>
  );
}

// Content
export type VideoPlayerContentProps = React.VideoHTMLAttributes<HTMLVideoElement>;

export const VideoPlayerContent = React.forwardRef<HTMLVideoElement, VideoPlayerContentProps>(function VContent(props, ref) {
  const { setVideo, actions } = useVideoCtx();
  const localRef = React.useRef<HTMLVideoElement>(null);
  // merge refs
  React.useImperativeHandle(ref, () => localRef.current as HTMLVideoElement);
  React.useEffect(() => {
    setVideo(localRef.current);
    return () => setVideo(null);
  }, [setVideo]);
  return (
    <video
      ref={localRef}
      {...props}
    />
  );
});

// Full-surface overlay to toggle play/pause on click.
// Renders behind the BigPlay button and in front of the <video>.
export function VideoPlayerSurfaceToggle({ className }: { className?: string }) {
  const { actions } = useVideoCtx();
  const onClick = React.useCallback(() => {
    // Avoid toggle misfires in fullscreen which can re-trigger play on some platforms
    const fsEl: any = document.fullscreenElement || (document as any).webkitFullscreenElement || (document as any).msFullscreenElement;
    if (fsEl) return; // Let native/fullscreen UI handle toggling
    actions.playPause();
  }, [actions]);
  return (
    <button
      type="button"
      aria-label="Toggle playback"
      onClick={onClick}
      className={className}
      style={{
        position: 'absolute',
        inset: 0,
        width: '100%',
        height: '100%',
        background: 'transparent',
        zIndex: 5,
        // Do not show focus ring by default for surface overlay
        outline: 'none',
      }}
    />
  );
}

// Control bar wrapper using tailwind, shadcn-friendly
export function VideoPlayerControlBar({ className, children, show = true }: React.PropsWithChildren<{ className?: string; show?: boolean }>) {
  const { controlsVisible } = useVideoCtx();
  if (!show) return null;
  const baseStyle: React.CSSProperties = {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 15,
    padding: '0.75rem',
    backgroundImage: 'linear-gradient(to top, rgb(var(--os-video-overlay, 0 0 0) / var(--os-video-overlay-alpha, 0.60)), transparent)',
    transition: 'opacity 200ms ease-out, transform 200ms ease-out',
    opacity: controlsVisible ? 1 : 0,
    transform: controlsVisible ? 'translateY(0)' : 'translateY(0.5rem)',
    pointerEvents: controlsVisible ? 'auto' : 'none',
  };
  return (
    <div className={className} style={baseStyle}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'rgb(var(--os-video-fg, 255 255 255))' }}>
        {children}
      </div>
    </div>
  );
}

// Buttons and ranges
export function VideoPlayerPlayButton({ className }: { className?: string }) {
  const { playing, actions } = useVideoCtx();
  const Icon = playing ? Pause : Play;
  return (
    <button
      type="button"
      aria-label={playing ? 'Pause' : 'Play'}
      onClick={actions.playPause}
      className={className}
      style={{ borderRadius: 6, padding: 8, background: 'transparent' }}
    >
      <Icon width={20} height={20} />
    </button>
  );
}

export function VideoPlayerSeekBackwardButton({ seconds = 10, className }: { seconds?: number; className?: string }) {
  const { actions } = useVideoCtx();
  return (
    <button
      type="button"
      aria-label={`Back ${seconds}s`}
      onClick={() => actions.seekBy(-Math.abs(seconds))}
      className={`rounded p-2 hover:bg-[color:rgb(var(--os-video-fg)/0.12)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:rgb(var(--os-video-ring))] focus-visible:ring-offset-2 ${className || ''}`}
    >
      <span className="text-xs">-{seconds}s</span>
    </button>
  );
}

export function VideoPlayerSeekForwardButton({ seconds = 10, className }: { seconds?: number; className?: string }) {
  const { actions } = useVideoCtx();
  return (
    <button
      type="button"
      aria-label={`Forward ${seconds}s`}
      onClick={() => actions.seekBy(Math.abs(seconds))}
      className={`rounded p-2 hover:bg-[color:rgb(var(--os-video-fg)/0.12)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:rgb(var(--os-video-ring))] focus-visible:ring-offset-2 ${className || ''}`}
    >
      <span className="text-xs">+{seconds}s</span>
    </button>
  );
}

export function VideoPlayerTimeDisplay({ showDuration = true, className }: { showDuration?: boolean; className?: string }) {
  const { currentTime, duration } = useVideoCtx();
  const format = (t: number) => {
    if (!Number.isFinite(t)) return '0:00';
    const h = Math.floor(t / 3600);
    const m = Math.floor((t % 3600) / 60);
    const s = Math.floor(t % 60);
    const mm = h > 0 ? String(m).padStart(2, '0') : String(m);
    const ss = String(s).padStart(2, '0');
    return h > 0 ? `${h}:${mm}:${ss}` : `${mm}:${ss}`;
  };
  return (
    <div className={`px-2 text-xs tabular-nums text-[color:rgb(var(--os-video-fg))]/90 ${className || ''}`} aria-live="off">
      {format(currentTime)}{showDuration ? ` / ${format(duration)}` : ''}
    </div>
  );
}

export function VideoPlayerTimeRange({ className }: { className?: string }) {
  const { currentTime, duration, actions } = useVideoCtx();
  const [focus, setFocus] = React.useState(false);
  const pct = duration > 0 ? (currentTime / duration) * 100 : 0;
  return (
    <div className={className} style={{ position: 'relative', flex: 1, paddingLeft: '0.5rem', paddingRight: '0.5rem' }}>
      <div
        className={focus ? undefined : undefined}
        style={{
          backgroundColor: 'rgb(var(--os-video-fg, 255 255 255) / 0.20)',
          height: '0.25rem',
          width: '100%',
          borderRadius: '0.25rem',
          boxShadow: focus ? '0 0 0 2px rgb(var(--os-video-ring, 255 255 255))' : undefined,
        }}
      >
        <div style={{ height: '0.25rem', borderRadius: '0.25rem', width: `${pct}%`, backgroundColor: 'rgb(var(--os-video-fg, 255 255 255))' }} />
      </div>
      <input
        type="range"
        min={0}
        max={Math.max(1, duration)}
        step={0.1}
        value={Math.min(duration, currentTime)}
        onChange={(e) => actions.seek(Number((e.target as HTMLInputElement).value))}
        onFocus={() => setFocus(true)}
        onBlur={() => setFocus(false)}
        className={undefined}
        style={{ position: 'absolute', inset: 0, height: '0.75rem', width: '100%', cursor: 'pointer', appearance: 'none', background: 'transparent', opacity: 0 }}
        aria-label="Seek"
      />
    </div>
  );
}

export function VideoPlayerMuteButton({ className }: { className?: string }) {
  const { muted, volume, actions } = useVideoCtx();
  const Icon = muted || volume === 0 ? VolumeX : volume < 0.5 ? Volume1 : Volume2;
  const nextMute = muted ? false : true;
  return (
    <button
      type="button"
      aria-label={nextMute ? 'Mute' : 'Unmute'}
      onClick={() => actions.setMuted(nextMute)}
      className={className}
      style={{ borderRadius: 6, padding: 8, background: 'transparent' }}
    >
      <Icon width={20} height={20} />
    </button>
  );
}

export function VideoPlayerVolumeRange({ className }: { className?: string }) {
  const { volume, actions } = useVideoCtx();
  return (
    <div className={`flex items-center gap-2 ${className || ''}`}>
      <input
        type="range"
        min={0}
        max={1}
        step={0.02}
        value={volume}
        onChange={(e) => actions.setVolume(Number((e.target as HTMLInputElement).value))}
        style={{ accentColor: 'rgb(var(--os-video-fg, 255 255 255))' }}
        className="w-24"
        aria-label="Volume"
      />
    </div>
  );
}

export function VideoPlayerFullscreenButton({ className }: { className?: string }) {
  const { actions } = useVideoCtx();
  return (
    <button
      type="button"
      aria-label="Fullscreen"
      onClick={actions.requestFullscreen}
      className={className}
      style={{ marginLeft: 'auto', borderRadius: 6, padding: 8, background: 'transparent' }}
    >
      <Maximize2 width={20} height={20} />
    </button>
  );
}

// Big centered play overlay
export function VideoPlayerBigPlayButton({ className }: { className?: string }) {
  const { playing, actions } = useVideoCtx();
  if (playing) return null;
  return (
    <button
      type="button"
      aria-label="Play"
      onClick={() => actions.playPause()}
      className={className}
      style={{
        position: 'absolute',
        left: '50%',
        top: '50%',
        transform: 'translate(-50%, -50%)',
        zIndex: 10,
        borderRadius: '9999px',
        padding: '1.25rem',
        WebkitBackdropFilter: 'blur(8px)',
        backdropFilter: 'blur(8px)',
        backgroundColor: 'rgb(var(--os-video-overlay, 0 0 0) / 0.5)',
        color: 'rgb(var(--os-video-fg, 255 255 255))',
      }}
    >
      <Play width={32} height={32} />
    </button>
  );
}

// Re-export icons if needed downstream
export { Play, Pause, Volume2, Volume1, VolumeX, Maximize2 };
